/* Mozaic Manufacturing — Deep Seating auto bucket-matching + shape upcharge
 *   + seat configuration (Seat Only vs Seat + Back) upcharge
 * ---------------------------------------------------------------------
 * Auto-selects the correct "Stock Size & Thickness Tier" option based on
 * customer-entered Width/Depth/Thickness, replicating the bucket-matching
 * logic from the "Deep Seating Quote" sheet (cells D26-D28) of the master
 * quoting tool (Into_The_Garden_Quoting_Tool.xlsx). Thickness stays a true
 * Number field in APO (required by the Fabric Cost calculation formula);
 * the Tier field is hidden from customers entirely and set automatically.
 *
 * Also computes the Seat Shape labor upcharge (Excel cells D49/D51),
 * replicating the "Shape upcharge rate" + "Shape upcharge on labor" logic:
 *   - Rectangle: no upcharge
 *   - Round (curved): +25% of the labor cost for the matched stock bucket
 *   - Custom (not a real stock shape): treated the same as an out-of-range
 *     size — forces a call-in instead of guessing a price
 * The dollar result is written into a hidden field ("Shape Upcharge"),
 * which has one trivial APO Calculation formula that prices it 1-for-1
 * (Increase price $ = Shape Upcharge). That field carries no price of its
 * own — only this script's computed value does.
 *
 * NEW: also computes the Seat Configuration upcharge (Excel cells
 * D22/D40-D43/D47/D48, "Deep Seating Quote" sheet). The option set's base
 * pricing (tier/labor/notions baked into the other calculations) assumes
 * "Seat Only". Selecting "Seat + Back" adds, on top of that baseline:
 *   - the labor delta between seat-only labor (70% of full-set) and
 *     full-set labor
 *   - a back cushion insert cost (looked up by matched bucket width +
 *     thickness — the insert is always priced as an 18in-tall stock
 *     block, independent of the actual back height, per the Excel sheet)
 *   - a high-back upcharge of $3.75/in for any back height over 22in
 *   - a back fabric cost (yardage computed from ACTUAL width + back
 *     height, with a 20% waste factor, at the same fabric rate used for
 *     the seat fabric cost)
 *   - a notions delta ($5 full set vs $3.125 seat-only)
 * This requires knowing the back cushion's actual height, so a new
 * customer-facing "Back height (in)" field was added; it's shown only
 * when "Seat + Back" is selected, and a missing/zero value in that case
 * forces the same call-in fallback as any other invalid input. Back
 * shape is not implemented (assumed Rectangle / 0% upcharge) — there is
 * no Back Shape field yet.
 * The Shape Upcharge calculation above also had to change: Excel's D51
 * (shape upcharge on labor) is based on D48 (labor), which itself depends
 * on Seat Configuration — so the labor figure used for the Shape Upcharge
 * is no longer hardcoded to the seat-only share; it uses whichever labor
 * base applies to the currently-selected Seat Configuration.
 * The new "Seat Config Adjustment" hidden field has its own trivial 1:1
 * Calculation formula, same pattern as Shape Upcharge.
 *
 * NEW: also computes the Piping + Tie Placement upcharge (Excel cells
 * D12-D14/D35/D44-D46). Three new customer-facing fields: "Piping?"
 * (Yes/No), "Tie placement" (None / Back corners only / All four
 * corners), and "Piping fabric" (Same as body fabric, or one of 10 named
 * contrast colors — shown only when Piping = Yes). When Piping = Yes,
 * this adds a piping/welt fabric + poly cord cost (perimeter-based
 * yardage, using actual Depth for Seat Only or actual Back height for
 * Seat + Back — the sheet swaps that dimension in directly) plus a flat
 * $15 contrast-color upcharge if a named color other than "Same as body
 * fabric" is chosen. Selecting any Tie placement other than "None" adds
 * a flat $10, independent of Piping. All of this is summed into a new
 * hidden "Piping & Tie Adjustment" field, with the same trivial 1:1
 * Calculation formula pattern as Shape Upcharge / Seat Config Adjustment.
 *
 * NEW: also computes Back Shape pricing (Excel cells D23/D50/D51), folded
 * into the existing "Shape Upcharge" field rather than a separate one —
 * that's exactly how the Excel sheet treats it. D51 ("shape upcharge on
 * labor") is ROUND(laborBase * MAX(seat shape rate, back shape rate), 2):
 * a single upcharge using whichever shape (seat or back) carries the
 * higher rate, not seat-plus-back summed. A new customer-facing "Back
 * Shape" field (Rectangle/Round/Custom — same 3-option simplification of
 * the Excel's fuller back-shape list already used for the live Seat
 * Shape field, and in fact Back Shape only HAS those same 3 options plus
 * "Other/custom" in the Excel sheet, so nothing was actually simplified
 * away here) is shown only when Seat Configuration = "Seat + Back" (back
 * shape is irrelevant, and always priced as 0, for Seat Only, per D50).
 * Selecting "Custom" for Back Shape while Seat + Back is selected forces
 * the same call-in fallback as an unpriceable Seat Shape, matching the
 * Excel D29 validity rule exactly.
 *
 * This is additive and self-contained: it only acts on option sets that
 * contain all the field names below, so it has zero effect on any
 * product that doesn't use this exact option set.
 *
 * Bucket-matching validated against the Excel formulas on 17
 * boundary/decimal/out-of-range test cases (see chat for the full test
 * log). Shape upcharge / seat configuration amounts are computed straight
 * from the "Labor Per Set", "Back Cushion Part Numbers", "Pricing
 * Calculator", and "Set Pricing Calculator" sheets.
 */
(function () {
  'use strict';
 
  var OPTION_IDS = {
    width: 'o_3539585',
    depth: 'o_3539586',
    thickness: 'o_3539587',
    shape: 'o_3539588',
    seatConfig: 'o_3539589',
    tier: 'o_3539602',
    shapeUpcharge: 'o_3540060',
    backHeight: 'o_3540397',
    seatConfigAdjustment: 'o_3540472',
    pipingTieAdjustment: 'o_3540517',
    piping: 'o_3540518',
    tiePlacement: 'o_3540519',
    pipingFabric: 'o_3540521',
    backShape: 'o_3540527',
  };
 
  // Exact replica of Excel cells D26/D27 (size bucket) and D28 (thickness
  // bucket), evaluated in the same order as the nested IFs.
  var SIZE_BUCKETS = [
    { maxW: 24, maxD: 24, bucketW: 24, bucketD: 24 },
    { maxW: 25, maxD: 28, bucketW: 25, bucketD: 28 },
    { maxW: 30, maxD: 28, bucketW: 30, bucketD: 28 },
    { maxW: 54, maxD: 28, bucketW: 54, bucketD: 28 },
    { maxW: 72, maxD: 28, bucketW: 72, bucketD: 28 },
    { maxW: 108, maxD: 40, bucketW: 108, bucketD: 40 },
  ];
  var THICKNESS_BUCKETS = [4, 5, 6, 7, 8];
 
  // Full-SET labor $ (seat + back together), straight from the "Labor Per
  // Set" sheet, keyed by "<bucketW>x<bucketD>" then thickness.
  var FULL_SET_LABOR = {
    '24x24': { 4: 40, 5: 44.375, 6: 48.75, 7: 53.125, 8: 57.5 },
    '25x28': { 4: 45, 5: 49.375, 6: 53.75, 7: 58.125, 8: 62.5 },
    '30x28': { 4: 52.5, 5: 56.875, 6: 61.25, 7: 65.625, 8: 70 },
    '54x28': { 4: 80, 5: 119.95, 6: 162.4, 7: 207.35, 8: 254.8 },
    '72x28': { 4: 307.25, 5: 362.2, 6: 419.65, 7: 479.6, 8: 542.05 },
    '108x40': { 4: 349.75, 5: 354.125, 6: 358.5, 7: 362.875, 8: 367.25 },
  };
  // "Pricing Calculator"!E11 — seat-only labor is this share of the
  // full-set labor figure (no separate seat-only labor table exists).
  var SEAT_LABOR_SHARE = 0.7;
 
  // "Back Cushion Part Numbers" sheet — insert cost, keyed by matched
  // bucket width then matched bucket thickness. Always an 18in-tall stock
  // block, independent of the actual back height (per the sheet's own
  // part-number scheme: "BACK-<w>x18x<t>").
  var BACK_INSERT = {
    24: { 4: 17.5, 5: 20, 6: 22.5, 7: 25, 8: 27.5 },
    25: { 4: 18.125, 5: 20.625, 6: 23.125, 7: 25.625, 8: 28.125 },
    30: { 4: 22.5, 5: 25, 6: 27.5, 7: 30, 8: 32.5 },
    54: { 4: 40, 5: 45, 6: 50, 7: 55, 8: 60 },
    72: { 4: 47.5, 5: 53.75, 6: 60, 7: 66.25, 8: 72.5 },
    108: { 4: 52.5, 5: 58.75, 6: 65, 7: 71.25, 8: 77.5 },
  };
 
  // "Set Pricing Calculator"!E11/E12 — high-back upcharge: $3.75/in over
  // 22in of back height (Excel D41).
  var HIGH_BACK_THRESHOLD_IN = 22;
  var HIGH_BACK_RATE_PER_IN = 3.75;
 
  // Back fabric yardage (Excel D42): (2 * actual width * actual back
  // height) / (54in bolt width * 36in/yd), with a 20% waste factor.
  var BACK_FABRIC_BOLT_WIDTH_IN = 54;
  var BACK_FABRIC_IN_PER_YD = 36;
  var BACK_FABRIC_WASTE_FACTOR = 1.2;
  // Same fabric rate used elsewhere for the seat fabric cost calculation.
  var BACK_FABRIC_RATE_PER_YD = 32.37;
 
  // "Pricing Calculator"!E10 and "Set Pricing Calculator"!E7 — notions,
  // Excel D47.
  var NOTIONS_SEAT_ONLY = 3.125;
  var NOTIONS_SEAT_BACK = 5;
 
  // "Set Pricing Calculator"!E14 (curved shapes). Rectangle has no
  // upcharge. Any shape not listed here (e.g. "Custom") is NOT priceable —
  // same as the Excel tool's "Other/custom forces a call-in" rule.
  var SHAPE_UPCHARGE_RATE = {
    Rectangle: 0,
    Round: 0.25,
  };
 
  var SEAT_ONLY_TEXT = 'Seat Only';
  var SEAT_BACK_TEXT = 'Seat + Back';
 
  // --- Piping + Tie Placement (Excel D12-D14/D35/D44-D46) ---
  // Piping welt strip width, "Set Pricing Calculator"!E8.
  var PIPING_STRIP_WIDTH_IN = 1.5;
  // Poly cord rate ($/yd), "Set Pricing Calculator"!E9.
  var POLY_CORD_RATE_PER_YD = 0.2;
  // Flat upcharge for a named contrast piping color (not "Same as body
  // fabric"), "Set Pricing Calculator"!E10.
  var CONTRAST_PIPING_UPCHARGE = 15;
  // Flat upcharge for any Tie placement other than "None", "Set Pricing
  // Calculator"!E15.
  var TIE_PLACEMENT_UPCHARGE = 10;
  var PIPING_YES_TEXT = 'Yes';
  var TIE_NONE_TEXT = 'None';
  var SAME_AS_BODY_FABRIC_TEXT = 'Same as body fabric';
  // "Piping Colors" sheet, rows 8-17 ($/yd). "Same as body fabric" and any
  // unrecognized value fall back to BACK_FABRIC_RATE_PER_YD (Excel D35's
  // IFERROR(...,$D$34) fallback — same known-flat rate used elsewhere).
  var PIPING_COLOR_RATES = {
    'Canvas Natural': 26.975,
    'Cast Silver': 26.4,
    'Canvas Navy': 26.975,
    'Canvas Charcoal': 26.975,
    'Canvas Black': 27.925,
    'Canvas Spa': 26.975,
    'Spectrum Indigo': 24.275,
    'Canvas Antique Beige': 26.975,
    'Canvas Forest Green': 26.975,
    'Canvas Henna': 31.25,
  };
 
  function matchSizeBucket(w, d) {
    for (var i = 0; i < SIZE_BUCKETS.length; i++) {
      var b = SIZE_BUCKETS[i];
      if (w <= b.maxW && d <= b.maxD) return { w: b.bucketW, d: b.bucketD };
    }
    return null;
  }
 
  function matchThicknessBucket(t) {
    for (var i = 0; i < THICKNESS_BUCKETS.length; i++) {
      if (t <= THICKNESS_BUCKETS[i]) return THICKNESS_BUCKETS[i];
    }
    return null;
  }
 
  function computeBucket(w, d, t) {
    if (!(w > 0) || !(d > 0) || !(t > 0)) return null;
    var size = matchSizeBucket(w, d);
    var thick = matchThicknessBucket(t);
    if (!size || thick == null) return null;
    return { w: size.w, d: size.d, t: thick };
  }
 
  function fullSetLaborFor(bucket) {
    var row = FULL_SET_LABOR[bucket.w + 'x' + bucket.d];
    if (!row) return null;
    var fullSet = row[bucket.t];
    return fullSet == null ? null : fullSet;
  }
 
  // Excel D48: labor base for the CURRENTLY selected seat configuration.
  // Seat Only = ROUND(70% of full-set, 2). Seat + Back = full-set as-is.
  function laborBaseFor(bucket, seatConfigText) {
    var fullSet = fullSetLaborFor(bucket);
    if (fullSet == null) return null;
    if (seatConfigText === SEAT_BACK_TEXT) return fullSet;
    return Math.round(fullSet * SEAT_LABOR_SHARE * 100) / 100;
  }
 
  function backInsertCostFor(bucket) {
    var row = BACK_INSERT[bucket.w];
    if (!row) return null;
    var cost = row[bucket.t];
    return cost == null ? null : cost;
  }
 
  function highBackUpcharge(backHeightIn) {
    if (!(backHeightIn > HIGH_BACK_THRESHOLD_IN)) return 0;
    return Math.round((backHeightIn - HIGH_BACK_THRESHOLD_IN) * HIGH_BACK_RATE_PER_IN * 100) / 100;
  }
 
  // Excel D42/D43: back fabric yardage (actual width + actual back
  // height, not bucketed), then cost.
  function backFabricCost(actualWidthIn, backHeightIn) {
    if (!(actualWidthIn > 0) || !(backHeightIn > 0)) return null;
    var rawYards =
      ((2 * actualWidthIn * backHeightIn) / (BACK_FABRIC_BOLT_WIDTH_IN * BACK_FABRIC_IN_PER_YD)) *
      BACK_FABRIC_WASTE_FACTOR;
    var yards = Math.round(rawYards * 1000) / 1000;
    return Math.round(yards * BACK_FABRIC_RATE_PER_YD * 100) / 100;
  }
 
  // Excel D35: welt/piping fabric rate.
  function weltRateFor(pipingFabricText) {
    if (!pipingFabricText || pipingFabricText === SAME_AS_BODY_FABRIC_TEXT) {
      return BACK_FABRIC_RATE_PER_YD;
    }
    if (Object.prototype.hasOwnProperty.call(PIPING_COLOR_RATES, pipingFabricText)) {
      return PIPING_COLOR_RATES[pipingFabricText];
    }
    return BACK_FABRIC_RATE_PER_YD; // unrecognized — same IFERROR fallback as Excel
  }
 
  // Excel D44: piping cost. secondDimIn is actual Depth (Seat Only) or
  // actual Back height (Seat + Back) — the formula swaps in Back height
  // for Depth when Seat + Back is selected, straight from the sheet.
  function pipingCost(actualWidthIn, secondDimIn, weltRate) {
    if (!(actualWidthIn > 0) || !(secondDimIn > 0)) return null;
    var perimeterIn = 2 * (actualWidthIn + secondDimIn) + 6;
    var fabricPart =
      ((PIPING_STRIP_WIDTH_IN * perimeterIn) / (BACK_FABRIC_BOLT_WIDTH_IN * BACK_FABRIC_IN_PER_YD)) * weltRate;
    var cordPart = (perimeterIn / BACK_FABRIC_IN_PER_YD) * POLY_CORD_RATE_PER_YD;
    return Math.round((fabricPart + cordPart) * 100) / 100;
  }
 
  function shapeRateFor(shapeText) {
    if (Object.prototype.hasOwnProperty.call(SHAPE_UPCHARGE_RATE, shapeText)) {
      return SHAPE_UPCHARGE_RATE[shapeText];
    }
    return null; // unrecognized / "Custom" — not priceable
  }
 
  function setNativeValue(el, value) {
    var proto = Object.getPrototypeOf(el);
    var desc = Object.getOwnPropertyDescriptor(proto, 'value');
    desc.set.call(el, value);
  }
 
  function fireEvents(el) {
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }
 
  function findTierOption(tierSelect, bucket) {
    if (!bucket) return null;
    var re = new RegExp('^' + bucket.w + 'x' + bucket.d + ', ' + bucket.t + 'in thick');
    var opts = tierSelect.options;
    for (var i = 0; i < opts.length; i++) {
      if (re.test(opts[i].text)) return opts[i];
    }
    return null;
  }
 
  function hideField(el) {
    var wrapper = el.closest('[data-option-id]');
    if (wrapper) {
      wrapper.style.display = 'none';
      wrapper.setAttribute('aria-hidden', 'true');
    }
  }
 
  function showField(el) {
    var wrapper = el.closest('[data-option-id]');
    if (wrapper) {
      wrapper.style.display = '';
      wrapper.removeAttribute('aria-hidden');
    }
  }
 
  function initForForm(form) {
    if (form.__mozaicAutoTierWired) return;
 
    var width = form.querySelector('input[name="mw_opts[' + OPTION_IDS.width + ']"]');
    var depth = form.querySelector('input[name="mw_opts[' + OPTION_IDS.depth + ']"]');
    var thick = form.querySelector('input[name="mw_opts[' + OPTION_IDS.thickness + ']"]');
    var shape = form.querySelector('select[name="mw_opts[' + OPTION_IDS.shape + ']"]');
    var seatConfig = form.querySelector('select[name="mw_opts[' + OPTION_IDS.seatConfig + ']"]');
    var tier = form.querySelector('select[name="mw_opts[' + OPTION_IDS.tier + ']"]');
    var upcharge = form.querySelector('input[name="mw_opts[' + OPTION_IDS.shapeUpcharge + ']"]');
    var backHeight = form.querySelector('input[name="mw_opts[' + OPTION_IDS.backHeight + ']"]');
    var seatAdjustment = form.querySelector(
      'input[name="mw_opts[' + OPTION_IDS.seatConfigAdjustment + ']"]'
    );
    var piping = form.querySelector('select[name="mw_opts[' + OPTION_IDS.piping + ']"]');
    var tiePlacement = form.querySelector('select[name="mw_opts[' + OPTION_IDS.tiePlacement + ']"]');
    var pipingFabric = form.querySelector('select[name="mw_opts[' + OPTION_IDS.pipingFabric + ']"]');
    var pipingTieAdjustment = form.querySelector(
      'input[name="mw_opts[' + OPTION_IDS.pipingTieAdjustment + ']"]'
    );
    var backShape = form.querySelector('select[name="mw_opts[' + OPTION_IDS.backShape + ']"]');
    if (
      !width || !depth || !thick || !shape || !seatConfig || !tier || !upcharge || !backHeight || !seatAdjustment ||
      !piping || !tiePlacement || !pipingFabric || !pipingTieAdjustment || !backShape
    ) {
      return; // not this option set — no-op
    }
 
    form.__mozaicAutoTierWired = true;
 
    // Hide the Tier and the hidden pricing fields from customers; they
    // stay in the form and still submit normally, they're just not shown.
    // Back height, Back Shape, and Piping fabric start hidden too — Back
    // height/Back Shape are only shown once "Seat + Back" is selected,
    // Piping fabric only once Piping is "Yes".
    hideField(tier);
    hideField(upcharge);
    hideField(seatAdjustment);
    hideField(backHeight);
    hideField(pipingTieAdjustment);
    hideField(pipingFabric);
    hideField(backShape);
 
    function run() {
      var w = parseFloat(width.value);
      var d = parseFloat(depth.value);
      var t = parseFloat(thick.value);
      var bucket = computeBucket(w, d, t);
 
      var shapeOpt = shape.options[shape.selectedIndex];
      var shapeText = shapeOpt ? shapeOpt.text : '';
      var shapeRate = shapeRateFor(shapeText);
 
      var seatConfigOpt = seatConfig.options[seatConfig.selectedIndex];
      var seatConfigText = seatConfigOpt ? seatConfigOpt.text : '';
      var isSeatBack = seatConfigText === SEAT_BACK_TEXT;
      var seatConfigValid = seatConfigText === SEAT_ONLY_TEXT || isSeatBack;
 
      // Show the Back height / Back Shape fields only when relevant.
      if (isSeatBack) {
        showField(backHeight);
        showField(backShape);
      } else {
        hideField(backHeight);
        hideField(backShape);
      }
 
      var backHeightVal = parseFloat(backHeight.value);
      var backHeightValid = !isSeatBack || backHeightVal > 0;
 
      // Excel D50: back shape rate is always 0 for Seat Only. For Seat +
      // Back, an unrecognized/"Custom" back shape is NOT priceable — same
      // call-in-for-custom-quote rule as an unpriceable Seat Shape (D29).
      var backShapeOpt = backShape.options[backShape.selectedIndex];
      var backShapeText = backShapeOpt ? backShapeOpt.text : '';
      var backShapeRate = isSeatBack ? shapeRateFor(backShapeText) : 0;
      var backShapeValid = !isSeatBack || backShapeRate != null;
 
      var pipingOpt = piping.options[piping.selectedIndex];
      var pipingText = pipingOpt ? pipingOpt.text : '';
      var isPipingYes = pipingText === PIPING_YES_TEXT;
 
      var tiePlacementOpt = tiePlacement.options[tiePlacement.selectedIndex];
      var tiePlacementText = tiePlacementOpt ? tiePlacementOpt.text : '';
 
      // Show the Piping fabric field only when it's actually relevant.
      if (isPipingYes) {
        showField(pipingFabric);
      } else {
        hideField(pipingFabric);
      }
 
      var pipingFabricOpt = pipingFabric.options[pipingFabric.selectedIndex];
      var pipingFabricText = pipingFabricOpt ? pipingFabricOpt.text : '';
 
      // A size out of range, an unrecognized/Custom shape, an unselected
      // Seat Configuration, or (when Seat + Back) a missing back height
      // all force the same "call in for a custom quote" path as the
      // Excel tool — clear the tier rather than guessing.
      var inRange = !!bucket && shapeRate != null && seatConfigValid && backHeightValid && backShapeValid;
      var match = inRange ? findTierOption(tier, bucket) : null;
 
      if (match) {
        if (tier.value !== match.value) {
          tier.value = match.value;
          fireEvents(tier);
        }
      } else if (tier.value !== '') {
        tier.value = '';
        fireEvents(tier);
      }
 
      var upchargeAmount = 0;
      var seatAdjAmount = 0;
      var pipingTieAmount = 0;
 
      if (match) {
        // Excel D51: ROUND(labor * MAX(seat shape rate, back shape
        // rate), 2) — one upcharge using whichever shape carries the
        // higher rate, not summed. backShapeRate is already forced to 0
        // for Seat Only, so this reduces to the seat-only behavior then.
        var effectiveShapeRate = Math.max(shapeRate || 0, backShapeRate || 0);
        if (effectiveShapeRate) {
          var laborBase = laborBaseFor(bucket, seatConfigText);
          if (laborBase != null) {
            upchargeAmount = Math.round(laborBase * effectiveShapeRate * 100) / 100;
          }
        }
 
        if (isSeatBack) {
          var fullSet = fullSetLaborFor(bucket);
          if (fullSet != null) {
            var seatOnlyLabor = Math.round(fullSet * SEAT_LABOR_SHARE * 100) / 100;
            var laborDelta = Math.round((fullSet - seatOnlyLabor) * 100) / 100;
            var insertCost = backInsertCostFor(bucket);
            var highBack = highBackUpcharge(backHeightVal);
            var fabricCost = backFabricCost(w, backHeightVal);
            var notionsDelta = Math.round((NOTIONS_SEAT_BACK - NOTIONS_SEAT_ONLY) * 100) / 100;
            if (insertCost != null && fabricCost != null) {
              seatAdjAmount =
                Math.round((laborDelta + insertCost + highBack + fabricCost + notionsDelta) * 100) / 100;
            }
          }
        }
 
        // Excel D44-D46: piping cost + contrast upcharge (only when
        // Piping = Yes) plus the flat tie-placement upcharge (independent
        // of Piping). secondDim swaps Depth for Back height on Seat +
        // Back, matching the sheet's own formula.
        if (isPipingYes) {
          var secondDim = isSeatBack ? backHeightVal : d;
          var weltRate = weltRateFor(pipingFabricText);
          var pCost = pipingCost(w, secondDim, weltRate);
          if (pCost != null) {
            pipingTieAmount += pCost;
            if (pipingFabricText && pipingFabricText !== SAME_AS_BODY_FABRIC_TEXT) {
              pipingTieAmount += CONTRAST_PIPING_UPCHARGE;
            }
          }
        }
        if (tiePlacementText && tiePlacementText !== TIE_NONE_TEXT) {
          pipingTieAmount += TIE_PLACEMENT_UPCHARGE;
        }
        pipingTieAmount = Math.round(pipingTieAmount * 100) / 100;
      }
 
      if (parseFloat(upcharge.value || '0') !== upchargeAmount) {
        setNativeValue(upcharge, upchargeAmount);
        fireEvents(upcharge);
      }
      if (parseFloat(seatAdjustment.value || '0') !== seatAdjAmount) {
        setNativeValue(seatAdjustment, seatAdjAmount);
        fireEvents(seatAdjustment);
      }
      if (parseFloat(pipingTieAdjustment.value || '0') !== pipingTieAmount) {
        setNativeValue(pipingTieAdjustment, pipingTieAmount);
        fireEvents(pipingTieAdjustment);
      }
    }
 
    [width, depth, thick, shape, seatConfig, backHeight, backShape, piping, tiePlacement, pipingFabric].forEach(function (el) {
      el.addEventListener('input', run);
      el.addEventListener('change', run);
    });
 
    run(); // handle any pre-filled values on load
  }
 
  function scan() {
    var forms = document.querySelectorAll('form.product-buy-buttons--form, form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) initForForm(forms[i]);
  }
 
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
 
  // APO can re-render the options form (e.g. on variant change) — keep
  // watching for it so the wiring survives that.
  if (window.MutationObserver) {
    new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
  }
})();